import { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { useAriaId } from '@components/component-helpers';
import {
  FileObject,
  InstantUploadFileInput,
  InstantUploadFileInputProps,
} from '@components/file-field/file-input';
import { FormLabelContainer, FormLabelContainerProps } from '@components/form-label/form-label';
import { IconButton } from '@components/icon-button';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconDropdown } from '@components/icons/dropdown';
import { IconUpload } from '@components/icons/upload';
import { PopoverButton } from '@components/popover-button';
import { Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { MakePropertiesOptional } from '@utils/types';

import { Avatar } from './avatars';
import {
  BORDER_RADIUS,
  BORDER_STYLE,
  DesignAvatar,
  DesignAvatarProps,
  getDesignAvatarUrl,
  getRandomAvatarUrl,
} from './design-avatar';

const AVATAR_PICKER_SIZE = 64;
const ALWAYS_ON_CLASSNAME = 'always-on';

export type DesignAvatarChooserProps = Pick<DesignAvatarProps, 'size'> &
  MakePropertiesOptional<Pick<InstantUploadFileInputProps, 'onSelect'>> &
  Pick<FormLabelContainerProps, 'label' | 'hideLabel'> & {
    avatars: Array<Avatar>;
    defaultImage?: string;
    onChange?(value: string): void;
  };

export const DesignAvatarChooser = ({
  avatars,
  onChange,
  onSelect,
  defaultImage,
  size = 64,
  label,
  hideLabel,
}: DesignAvatarChooserProps) => {
  const [url, setUrl] = useState(defaultImage ?? '');

  useEffect(() => {
    if (!defaultImage) {
      const randomAvatarUrl = getRandomAvatarUrl();
      setUrl(randomAvatarUrl);
      onChange?.(randomAvatarUrl);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack orientation="horizontal" space={4} align="center">
      <PopoverButton
        trigger={IconButton}
        triggerProps={{
          icon: (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Overlay />
              <DesignAvatar src={url} size={size} />
            </div>
          ),
          style: { width: 'auto', height: 'auto' },
        }}
        contentStyle={{ maxWidth: 'max-content' }}
      >
        {({ close }) => {
          const handleChange = (newUrl: string = '', newPath?: string) => {
            setUrl(newUrl);
            onChange?.(newPath ?? newUrl);
            close();
          };

          return (
            <Grid>
              {avatars.map((avatar) => {
                const src = getDesignAvatarUrl(avatar);
                const isSelected = url.includes(avatar);
                return (
                  <div
                    key={avatar}
                    onClick={() => handleChange(src)}
                    style={{ cursor: 'pointer', position: 'relative', display: 'inline-block' }}
                  >
                    <Overlay
                      isAlwaysOn={isSelected}
                      icon={<IconCheckmark size={24} color="elementWhiteInvert" />}
                    />
                    <DesignAvatar key={avatar} src={src} size={AVATAR_PICKER_SIZE} />
                  </div>
                );
              })}
              {onSelect && (
                <UploadButton
                  onSelect={onSelect}
                  onChange={(file) => handleChange(file?.fullUrl, file?.url)}
                />
              )}
            </Grid>
          );
        }}
      </PopoverButton>
      <FormLabelContainer label={label} hideLabel={hideLabel} />
    </Stack>
  );
};

type OverlayProps = {
  icon?: React.ReactNode;
  isAlwaysOn?: boolean;
};

const Overlay = ({
  icon = <IconDropdown size={24} color="elementWhiteInvert" />,
  isAlwaysOn = false,
}: OverlayProps) => {
  return <StyledOverlay className={isAlwaysOn ? ALWAYS_ON_CLASSNAME : ''}>{icon}</StyledOverlay>;
};

const StyledOverlay = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.color.uiTrans80,
  borderRadius: BORDER_RADIUS,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  zIndex: 1,
  transition: 'opacity 0.2s ease-in-out',
  [`&:hover, &.${ALWAYS_ON_CLASSNAME}`]: {
    opacity: 1,
  },
});

type UploadButtonProps = Pick<InstantUploadFileInputProps, 'onSelect' | 'onChange'>;

const UploadButton = ({ onSelect, onChange }: UploadButtonProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputId = useAriaId('file');

  const handleChange = (newFile: FileObject | null) => {
    if (newFile) {
      onChange?.(newFile);
    }
  };

  return (
    <>
      <InstantUploadFileInput
        ref={fileInputRef}
        id={inputId}
        fileType="image"
        onSelect={onSelect}
        onChange={handleChange}
      />
      <StyledUploadButton
        icon={<IconUpload size={24} />}
        variant="secondary-purple"
        onClick={() => fileInputRef?.current?.click()}
      />
    </>
  );
};

const StyledUploadButton = styled(IconButton)({
  '&&&': {
    width: pxToRem(AVATAR_PICKER_SIZE),
    height: pxToRem(AVATAR_PICKER_SIZE),
    borderRadius: BORDER_RADIUS,
    border: BORDER_STYLE,
  },
});

const Grid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: pxToRem(12),
  width: 'fit-content',
});
